// PaatiFormatosTable.jsx
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  Button,
  Tag,
  Modal,
  Upload,
  Space,
  Typography,
  Card,
  message,
  Row,
  Col,
  Breadcrumb,
  Alert,
  Input,
  Select,
  Tooltip,
} from "antd";
import {
  UploadOutlined,
  FileTextOutlined,
  CheckCircleFilled,
  ClockCircleFilled,
  InboxOutlined,
  ArrowLeftOutlined,
  EyeOutlined,
  HomeOutlined,
  DownloadOutlined,
  CheckOutlined,
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
  ClearOutlined,
  FilterOutlined,
} from "@ant-design/icons";

// ─── Imports de formularios por formato ──────────────────────────────────────
import ActaFinalPDF from "./actaFinalizacion/PdfActaFinal";
import FormActaFinalizacion from "./actaFinalizacion/FormActaFinalizacion";
import { useSelector } from "react-redux";
import {
  getAllParticipantesFormatos,
  getDescargarFormato,
  subirFormatoPaati,
} from "../../../services/paati/actasFormatos/paatiFormatos";
import Header166 from "../../header/header";

/**
 * REGISTRO CENTRAL DE FORMATOS
 */
const REGISTRO_FORMATOS = {
  ACTA_FINALIZACION: { pdf: ActaFinalPDF, form: FormActaFinalizacion },
};

const { Text, Title } = Typography;
const { Dragger } = Upload;
const { Option } = Select;

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("es-CO", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// ─── Configuración visual por estado ─────────────────────────────────────────
const ESTADO_CONFIG = {
  SIN_DATOS: {
    tagColor: "default",
    label: "SIN DATOS",
    icon: <ClockCircleFilled style={{ color: "#bfbfbf" }} />,
  },
  PENDIENTE_PDF: {
    tagColor: "warning",
    label: "PENDIENTE PDF",
    icon: <ClockCircleFilled style={{ color: "#faad14" }} />,
  },
  FINALIZADO: {
    tagColor: "success",
    label: "FINALIZADO",
    icon: <CheckCircleFilled style={{ color: "#52c41a" }} />,
  },
};

// ─── Modal de carga de archivo ────────────────────────────────────────────────
function ModalCargaArchivo({
  open,
  formato,
  participante,
  onClose,
  onSuccess,
  username,
}) {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  const obtenerMesNumero = (mes) => {
    const meses = {
      enero: "01",
      febrero: "02",
      marzo: "03",
      abril: "04",
      mayo: "05",
      junio: "06",
      julio: "07",
      agosto: "08",
      septiembre: "09",
      octubre: "10",
      noviembre: "11",
      diciembre: "12",
    };

    return meses[mes.toLowerCase()] || "00";
  };
  const handleUpload = async () => {
    if (!fileList.length) {
      message.warning("Selecciona un archivo antes de continuar");
      return;
    }
    if (!username) {
      message.error("No se puede cargar el archivo: usuario no identificado");
      return;
    }

    setUploading(true);
    try {
      const formData = participante.formatos?.[0]?.form_data;
      const mes = formData?.mes_firma;
      const vigencia = formData?.vigencia_firma;
      const dia = formData?.dia_firma;
      // Capitalizar primera letra del mes
      const mesCapitalizado = mes
        ? mes.charAt(0).toUpperCase() + mes.slice(1).toLowerCase()
        : "Mes"; // Mes numérico (para archivo)
      const mesNumero = obtenerMesNumero(mes);

      const rutaArchivo = `${mesCapitalizado}_${vigencia}/${participante.departamento}/${participante.codigo_pre_dir}/`;

      const nombreArchivo = `1_Acta_Finalizacion_${vigencia}_${mesNumero}_${dia}.pdf`;

      const formDataToSend = new FormData();

      formDataToSend.append("coido_sir", participante.codigo_sir);
      formDataToSend.append("oidTipoFormato", "1");
      formDataToSend.append("cargado_por", username); // opcional
      formDataToSend.append("ruta", rutaArchivo); // opcional
      formDataToSend.append("nombre_archivo", nombreArchivo);
      formDataToSend.append("file", fileList[0].originFileObj, nombreArchivo); // archivo PDF
      const result = await subirFormatoPaati(formDataToSend, participante.oid);

      message.success(result.mensaje ?? "Archivo cargado correctamente");
      onSuccess(participante.oid, formato.oid_tipo_formato, fileList[0].name);
      setFileList([]);
      onClose();
    } catch (error) {
      const msg =
        error?.response?.data?.mensaje ?? "Error al cargar el archivo";
      message.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        setFileList([]);
        onClose();
      }}
      title={
        <Space>
          <UploadOutlined style={{ color: "#1677ff" }} />
          <span>Cargar documento — {formato?.nombre}</span>
        </Space>
      }
      width={520}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            setFileList([]);
            onClose();
          }}
        >
          Cancelar
        </Button>,
        <Button
          key="upload"
          type="primary"
          loading={uploading}
          disabled={!fileList.length}
          onClick={handleUpload}
          icon={<UploadOutlined />}
        >
          {uploading ? "Cargando..." : "Confirmar carga"}
        </Button>,
      ]}
      destroyOnClose
    >
      <Space
        direction="vertical"
        style={{ width: "100%", paddingTop: 8 }}
        size="middle"
      >
        <Alert
          type="info"
          showIcon
          message={
            <Text strong>
              {participante?.nombre_if} - {participante?.codigo_sir}
            </Text>
          }
          description={`Vas a cargar el documento: ${formato?.nombre}`}
        />
        <Dragger
          fileList={fileList}
          beforeUpload={() => false}
          onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
          accept=".pdf"
          maxCount={1}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined style={{ color: "#1677ff" }} />
          </p>
          <p className="ant-upload-text">Haz clic o arrastra el archivo aquí</p>
          <p className="ant-upload-hint">PDF · Máximo 1 archivo</p>
        </Dragger>
        {fileList.length > 0 && (
          <Alert
            type="success"
            showIcon
            icon={<CheckOutlined />}
            message={`Archivo seleccionado: ${fileList[0].name}`}
          />
        )}
      </Space>
    </Modal>
  );
}

// ─── Vista PDF (preview + descarga) ──────────────────────────────────────────
function VistaPDF({ proyecto, formato, onRegresar }) {
  const ComponentePDF = REGISTRO_FORMATOS[formato.codigo]?.pdf;
  const pdfRef = useRef(null);
  const [descargando, setDescargando] = useState(false);

  const handleDescargar = async () => {
    if (!pdfRef.current?.descargarPDF) {
      message.warning("Este componente aún no expone descargarPDF");
      return;
    }
    setDescargando(true);
    try {
      await pdfRef.current.descargarPDF();
    } catch {
      message.error("Error al generar el PDF");
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <>
                <HomeOutlined /> Proyectos
              </>
            ),
            onClick: onRegresar,
            href: "#",
          },
          { title: proyecto.codigo_interno },
          { title: formato.nombre },
        ]}
      />
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={16} wrap={false}>
          <Col flex="none">
            <Button icon={<ArrowLeftOutlined />} onClick={onRegresar}>
              Regresar
            </Button>
          </Col>
          <Col flex="auto">
            <Text strong style={{ fontSize: 15 }}>
              {formato.nombre}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {proyecto.codigo_interno} · {proyecto.nombre_profesional_gse} ·{" "}
              {proyecto.proyecto_municipio}
            </Text>
          </Col>
          {ComponentePDF && (
            <Col flex="none">
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                loading={descargando}
                onClick={handleDescargar}
              >
                {descargando ? "Generando PDF..." : "Descargar PDF"}
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {ComponentePDF ? (
        <ComponentePDF
          ref={pdfRef}
          proyectoId={proyecto.oid}
          tipoFormatoID={formato.oid_tipo_formato}
          onClose={onRegresar}
          codigo_interno={proyecto.codigo_interno}
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Componente PDF no registrado"
          description={`Agrega el componente PDF para "${formato.codigo}" en REGISTRO_FORMATOS.`}
        />
      )}
    </div>
  );
}

// ─── Vista Formulario ─────────────────────────────────────────────────────────
function VistaFormulario({ proyecto, formato, onRegresar, onUpdateDataLocal }) {
  const ComponenteForm = REGISTRO_FORMATOS[formato.codigo]?.form;

  return (
    <div style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <>
                <HomeOutlined /> Proyectos
              </>
            ),
            onClick: onRegresar,
            href: "#",
          },
          { title: proyecto.codigo_interno },
          { title: formato.nombre },
        ]}
      />
      <Card style={{ marginBottom: 16 }}>
        <Row align="middle" gutter={16} wrap={false}>
          <Col flex="none">
            <Button icon={<ArrowLeftOutlined />} onClick={onRegresar}>
              Regresar
            </Button>
          </Col>
          <Col flex="auto">
            <Text strong style={{ fontSize: 15 }}>
              {formato.nombre}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 13 }}>
              {proyecto.codigo_interno} · {proyecto.nombre_profesional_gse} ·{" "}
              {proyecto.proyecto_municipio}
            </Text>
          </Col>
        </Row>
      </Card>

      {ComponenteForm ? (
        <ComponenteForm
          proyectoId={proyecto.oid}
          tipoFormatoID={formato.oid_tipo_formato}
          onClose={onRegresar}
          updateDataLocal={(formData) => {
            // Se pasa formData para que el estado local quede completo
            // y el modal de carga de PDF funcione sin necesidad de recargar
            onUpdateDataLocal(proyecto.oid, formato.oid_tipo_formato, formData);
          }}
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          message="Formulario no registrado"
          description={`Agrega el formulario para "${formato.codigo}" en REGISTRO_FORMATOS.`}
        />
      )}
    </div>
  );
}

// ─── Cabecera de Filtros ──────────────────────────────────────────────────────
function FiltrosCabecera({
  participantes,
  filtros,
  onFiltrosChange,
  onActualizar,
  onLimpiar,
  loading,
}) {
  // Opciones únicas derivadas de los datos
  const departamentos = [
    ...new Set(participantes.map((p) => p.departamento).filter(Boolean)),
  ].sort();
  const municipios = [
    ...new Set(
      participantes
        .filter(
          (p) =>
            !filtros.departamento || p.departamento === filtros.departamento,
        )
        .map((p) => p.municipio)
        .filter(Boolean),
    ),
  ].sort();

  const hayFiltrosActivos =
    filtros.codigo_sir ||
    filtros.departamento ||
    filtros.municipio ||
    filtros.estado;

  return (
    <Card
      style={{
        margin: "16px 20px 0",
        borderRadius: "12px 12px 0 0",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        borderBottom: "2px solid #e6f4ff",
        background: "linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)",
      }}
      bodyStyle={{ padding: "16px 20px" }}
    >
      {/* Título de sección filtros */}
      <Row align="middle" style={{ marginBottom: 14 }}>
        <Col>
          <Space size={8}>
            <FilterOutlined style={{ color: "#1677ff", fontSize: 15 }} />
            <Text strong style={{ fontSize: 14, color: "#1677ff" }}>
              Filtros de búsqueda
            </Text>
            {hayFiltrosActivos && (
              <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>
                Filtros activos
              </Tag>
            )}
          </Space>
        </Col>
      </Row>

      {/* Fila de controles */}
      <Row gutter={[12, 12]} align="bottom">
        {/* Código SIR */}
        <Col xs={24} sm={12} md={6}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 500 }}>
              Código SIR
            </Text>
            <Input
              placeholder="Buscar código SIR..."
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filtros.codigo_sir}
              onChange={(e) =>
                onFiltrosChange({ ...filtros, codigo_sir: e.target.value })
              }
              allowClear
              style={{ borderRadius: 8 }}
            />
          </Space>
        </Col>

        {/* Departamento */}
        <Col xs={24} sm={12} md={6}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 500 }}>
              Departamento
            </Text>
            <Select
              placeholder="Seleccionar departamento"
              value={filtros.departamento || undefined}
              onChange={(val) =>
                onFiltrosChange({
                  ...filtros,
                  departamento: val ?? "",
                  municipio: "",
                })
              }
              allowClear
              style={{ width: "100%", borderRadius: 8 }}
              showSearch
              optionFilterProp="children"
            >
              {departamentos.map((d) => (
                <Option key={d} value={d}>
                  {d}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>

        {/* Municipio */}
        <Col xs={24} sm={12} md={6}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 500 }}>
              Municipio
            </Text>
            <Select
              placeholder="Seleccionar municipio"
              value={filtros.municipio || undefined}
              onChange={(val) =>
                onFiltrosChange({ ...filtros, municipio: val ?? "" })
              }
              allowClear
              style={{ width: "100%", borderRadius: 8 }}
              showSearch
              optionFilterProp="children"
              disabled={!filtros.departamento}
            >
              {municipios.map((m) => (
                <Option key={m} value={m}>
                  {m}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>

        {/* Estado */}
        <Col xs={24} sm={12} md={6}>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Text style={{ fontSize: 12, color: "#595959", fontWeight: 500 }}>
              Estado
            </Text>
            <Select
              placeholder="Seleccionar estado"
              value={filtros.estado || undefined}
              onChange={(val) =>
                onFiltrosChange({ ...filtros, estado: val ?? "" })
              }
              allowClear
              style={{ width: "100%", borderRadius: 8 }}
            >
              <Option value="SIN_DATOS">
                <Space size={6}>
                  <ClockCircleFilled style={{ color: "#bfbfbf" }} />
                  Sin datos
                </Space>
              </Option>
              <Option value="PENDIENTE_PDF">
                <Space size={6}>
                  <ClockCircleFilled style={{ color: "#faad14" }} />
                  Pendiente PDF
                </Space>
              </Option>
              <Option value="FINALIZADO">
                <Space size={6}>
                  <CheckCircleFilled style={{ color: "#52c41a" }} />
                  Finalizado
                </Space>
              </Option>
            </Select>
          </Space>
        </Col>
      </Row>

      {/* Fila de botones */}
      <Row justify="end" style={{ marginTop: 14 }} gutter={8}>
        <Col>
          <Tooltip title="Limpiar todos los filtros">
            <Button
              icon={<ClearOutlined />}
              onClick={onLimpiar}
              disabled={!hayFiltrosActivos}
              style={{
                borderRadius: 8,
                borderColor: hayFiltrosActivos ? "#ff4d4f" : undefined,
                color: hayFiltrosActivos ? "#ff4d4f" : undefined,
              }}
            >
              Limpiar
            </Button>
          </Tooltip>
        </Col>
        <Col>
          <Tooltip title="Recargar datos desde el servidor">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={onActualizar}
              style={{ borderRadius: 8 }}
            >
              Actualizar
            </Button>
          </Tooltip>
        </Col>
      </Row>
    </Card>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function PaatiFormatosTable({
  tipo_formato,
  titulo,
  type_header,
}) {
  const [participantes, setParticipantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vistaActiva, setVistaActiva] = useState(null);
  const [modalCarga, setModalCarga] = useState({
    open: false,
    formato: null,
    proyecto: null,
  });
  const usernameFromState = useSelector((state) => state.auth.user);

  // ─── Estado de filtros ──────────────────────────────────────────────────────
  const FILTROS_VACIOS = {
    codigo_sir: "",
    departamento: "",
    municipio: "",
    estado: "",
  };
  const [filtros, setFiltros] = useState(FILTROS_VACIOS);

  // ─── Carga de datos ─────────────────────────────────────────────────────────
  const fetchParticipantes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllParticipantesFormatos(tipo_formato);
      if (response.success) {
        setParticipantes(response?.data || []);
      } else {
        setParticipantes([]);
      }
    } catch (error) {
      console.error("Error cargando participantes:", error);
      message.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  }, [tipo_formato]);

  useEffect(() => {
    fetchParticipantes();
  }, [fetchParticipantes]);

  // ─── Lógica de filtrado ─────────────────────────────────────────────────────
  const participantesFiltrados = participantes.filter((p) => {
    const matchSir =
      !filtros.codigo_sir ||
      p.codigo_sir?.toLowerCase().includes(filtros.codigo_sir.toLowerCase());
    const matchDep =
      !filtros.departamento || p.departamento === filtros.departamento;
    const matchMun = !filtros.municipio || p.municipio === filtros.municipio;
    const matchEstado =
      !filtros.estado || p.formatos?.[0]?.estado === filtros.estado;

    return matchSir && matchDep && matchMun && matchEstado;
  });

  const handleLimpiarFiltros = () => setFiltros(FILTROS_VACIOS);

  // ─── Actualización de estado local ─────────────────────────────────────────
  const actualizarEstadoFormatoForm = (
    proyectoOid,
    tipoFormatoId,
    formData,
  ) => {
    setParticipantes((prev) =>
      prev.map((p) => {
        if (p.oid !== proyectoOid) return p;
        return {
          ...p,
          formatos: p.formatos?.map((f) => {
            if (f.oid_tipo_formato !== tipoFormatoId) return f;
            return {
              ...f,
              // Solo avanza de SIN_DATOS → PENDIENTE_PDF, nunca retrocede
              estado: f.estado === "SIN_DATOS" ? "PENDIENTE_PDF" : f.estado,
              // Siempre actualiza form_data para que el modal de carga
              // tenga mes_firma, vigencia_firma y dia_firma al instante,
              // tanto en primera edición como en re-ediciones
              ...(formData ? { form_data: formData } : {}),
            };
          }),
        };
      }),
    );
  };

  const handleUploadSuccess = (proyectoOid, tipoFormatoOid, fileName) => {
    setParticipantes((prev) =>
      prev.map((p) => {
        if (p.oid !== proyectoOid) return p;
        return {
          ...p,
          formatos: p.formatos?.map((f) =>
            f.oid_tipo_formato === tipoFormatoOid
              ? {
                  ...f,
                  estado: "FINALIZADO",
                  nombre_archivo: fileName,
                  fecha_carga: new Date().toISOString().split("T")[0],
                  cargado_por: usernameFromState?.username ?? "usuario",
                }
              : f,
          ),
        };
      }),
    );
  };

  const handleRegresar = () => setVistaActiva(null);

  // ─── Vista PDF ──────────────────────────────────────────────────────────────
  if (vistaActiva?.tipo === "pdf") {
    return (
      <VistaPDF
        proyecto={vistaActiva.proyecto}
        formato={vistaActiva.formato}
        onRegresar={handleRegresar}
      />
    );
  }

  // ─── Vista Formulario ───────────────────────────────────────────────────────
  if (vistaActiva?.tipo === "form") {
    return (
      <VistaFormulario
        proyecto={vistaActiva.proyecto}
        formato={vistaActiva.formato}
        onRegresar={handleRegresar}
        onUpdateDataLocal={actualizarEstadoFormatoForm}
      />
    );
  }

  // ─── Columnas ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Acciones",
      key: "acciones",
      width: 130,
      render: (_, participante) => {
        const fmt = participante.formatos?.[0] ?? {};
        const estado = fmt.estado;

        return (
          <Space size="small">
            {estado === "SIN_DATOS" && (
              <Tooltip title="Editar">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() =>
                    setVistaActiva({
                      tipo: "form",
                      proyecto: participante,
                      formato: fmt,
                    })
                  }
                />
              </Tooltip>
            )}

            {estado === "PENDIENTE_PDF" && (
              <>
                <Tooltip title="Editar">
                  <Button
                    shape="circle"
                    icon={<EditOutlined />}
                    size="small"
                    onClick={() =>
                      setVistaActiva({
                        tipo: "form",
                        proyecto: participante,
                        formato: fmt,
                      })
                    }
                  />
                </Tooltip>

                <Tooltip title="Descargar PDF">
                  <Button
                    shape="circle"
                    icon={<DownloadOutlined />}
                    size="small"
                    onClick={() =>
                      setVistaActiva({
                        tipo: "pdf",
                        proyecto: participante,
                        formato: fmt,
                      })
                    }
                  />
                </Tooltip>

                <Tooltip title="Cargar PDF">
                  <Button
                    type="primary"
                    shape="circle"
                    icon={<UploadOutlined />}
                    size="small"
                    onClick={() =>
                      setModalCarga({
                        open: true,
                        formato: fmt,
                        proyecto: participante,
                      })
                    }
                  />
                </Tooltip>
              </>
            )}

            {estado === "FINALIZADO" && (
              <Tooltip title="Ver PDF">
                <Button
                  type="primary"
                  shape="circle"
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={() =>
                    getDescargarFormato(participante.oid, fmt.oid_tipo_formato)
                  }
                />
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "Estado",
      width: 160,
      render: (_, participante) => {
        const estado = participante.formatos?.[0]?.estado;
        const cfg = ESTADO_CONFIG[estado] ?? ESTADO_CONFIG.SIN_DATOS;
        return (
          <Space size={6}>
            {cfg.icon}
            <Tag
              color={cfg.tagColor}
              style={{ fontWeight: 700, fontSize: 11, margin: 0 }}
            >
              {cfg.label}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Código SIR",
      dataIndex: "codigo_sir",
      width: 145,
      render: (v) => (
        <Text code style={{ fontSize: 12 }}>
          {v}
        </Text>
      ),
    },
    { title: "Nombre", dataIndex: "nombre_if", ellipsis: true },
    {
      title: "Código PRE",
      dataIndex: "codigo_pre",
      ellipsis: true,
      width: 130,
    },
    { title: "Departamento", dataIndex: "departamento", width: 130 },
    { title: "Municipio", dataIndex: "municipio", width: 130 },
    { title: "Grupo territorial", dataIndex: "grupo_territorial", width: 140 },
    { title: "Modalidad", dataIndex: "tipo_forma", width: 140 },
  ];

  // ─── Vista tabla principal ──────────────────────────────────────────────────
  return (
    <div className="container">
      <Header166 type={type_header} />
      {/* Cabecera de filtros */}
      <FiltrosCabecera
        participantes={participantes}
        filtros={filtros}
        onFiltrosChange={setFiltros}
        onActualizar={fetchParticipantes}
        onLimpiar={handleLimpiarFiltros}
        loading={loading}
      />
      ´{/* Tabla */}
      <Card
        style={{
          margin: "0 20px 20px",
          borderRadius: "0 0 12px 12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        }}
        bodyStyle={{ paddingTop: 0 }}
      >
        {/* Resumen de resultados */}
        <Row
          align="middle"
          justify="space-between"
          style={{
            padding: "12px 0 8px",
            borderBottom: "1px solid #f0f0f0",
            marginBottom: 8,
          }}
        >
          <Col>
            <Space size={6}>
              <Text type="secondary" style={{ fontSize: 13 }}>
                Mostrando{" "}
                <Text strong style={{ color: "#1677ff" }}>
                  {participantesFiltrados.length}
                </Text>{" "}
                de <Text strong>{participantes.length}</Text> participantes
              </Text>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={participantesFiltrados}
          rowKey="oid"
          loading={loading}
          size="small"
          scroll={{ y: 500, x: "max-content" }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} participantes`,
          }}
        />
      </Card>
      <ModalCargaArchivo
        open={modalCarga.open}
        formato={modalCarga.formato}
        participante={modalCarga.proyecto}
        onClose={() =>
          setModalCarga({ open: false, formato: null, proyecto: null })
        }
        onSuccess={handleUploadSuccess}
        username={usernameFromState?.username}
      />
    </div>
  );
}
